import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { formatApiError } from "../lib/api";
import { ArrowLeft } from "lucide-react";

export default function Register() {
  const { register, verifyOtp, user } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) { navigate("/dashboard", { replace: true }); return null; }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    
    try {
      if (step === 1) {
        if (password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
        const res = await register(name, email, password);
        if (res.require_otp) {
          setStep(2);
        } else {
          navigate("/dashboard");
        }
      } else {
        if (otp.length !== 6) { setError("Verification code must be 6 digits"); setLoading(false); return; }
        await verifyOtp(name, email, password, otp);
        navigate("/dashboard");
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F0F0] flex">
      <div className="hidden lg:flex lg:w-1/2 bg-[#050505] border-r border-[#1E1E1E] p-12 flex-col justify-between">
        <div>
          <Link to="/" className="font-heading text-xl font-black tracking-tighter uppercase" data-testid="register-logo">
            NRD<span className="text-[#00FF41]">.</span>
          </Link>
        </div>
        <div>
          <h2 className="font-heading text-4xl font-black tracking-tighter uppercase mb-4">Start<br />Building<br /><span className="text-[#00FF41]">Today</span></h2>
          <p className="text-slate-500 text-sm max-w-sm">Join hundreds of teams automating their commerce operations.</p>
        </div>
        <p className="text-xs text-slate-700">&copy; 2025 NoRespectDigital</p>
      </div>

      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm">
          <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-[#00FF41] mb-8 transition-colors" data-testid="register-back">
            <ArrowLeft size={14} /> Back
          </Link>
          <h1 className="font-heading text-3xl font-bold tracking-tight mb-2" data-testid="register-title">Create Account</h1>
          <p className="text-sm text-slate-500 mb-8">Start your 14-day free trial. No credit card required.</p>

          {error && (
            <div className="mb-4 p-3 bg-red-900/30 border border-red-800 text-sm text-red-400" data-testid="register-error">
              {error}
            </div>
          )}

          {step === 1 ? (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Name</label>
                <input
                  type="text" required value={name} onChange={e => setName(e.target.value)}
                  className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Email</label>
                <input
                  type="email" required value={email} onChange={e => setEmail(e.target.value)}
                  className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Password</label>
                <input
                  type="password" required value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-sm text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                  data-testid="register-password-input"
                />
                <p className="text-xs text-slate-600 mt-1">Minimum 6 characters</p>
              </div>
              <button
                type="submit" disabled={loading}
                className="w-full bg-[#00FF41] text-[#0A0A0A] py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
                data-testid="register-submit-button"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="otp-form">
              <div className="p-4 bg-[#111111] border border-[#1E1E1E] mb-6">
                <p className="text-sm text-slate-400">We've sent a 6-digit verification code to <strong className="text-[#F0F0F0]">{email}</strong>.</p>
              </div>
              <div>
                <label className="text-xs font-mono uppercase tracking-[0.2em] text-slate-500 mb-1.5 block">Verification Code</label>
                <input
                  type="text" required value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  className="w-full bg-[#111111] border border-[#1E1E1E] px-4 py-3 text-center text-2xl tracking-[0.5em] font-mono text-[#F0F0F0] focus:outline-none focus:border-[#00FF41] transition-colors"
                  data-testid="register-otp-input"
                />
              </div>
              <button
                type="submit" disabled={loading || otp.length !== 6}
                className="w-full bg-[#00FF41] text-[#0A0A0A] py-3 text-sm font-bold hover:bg-[#00CC33] transition-colors disabled:opacity-50"
              >
                {loading ? "Verifying..." : "Verify & Complete Setup"}
              </button>
              <button
                type="button" onClick={() => setStep(1)}
                className="w-full bg-transparent border border-[#1E1E1E] text-slate-400 py-3 text-sm font-bold hover:text-[#F0F0F0] transition-colors mt-2"
              >
                Back to Registration
              </button>
            </form>
          )}

          <p className="mt-6 text-sm text-slate-500 text-center">
            Already have an account? <Link to="/login" className="text-[#00FF41] font-medium hover:text-[#00CC33]" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
